<template>
  <div
      class="drawing-board-container"
      v-show="visible"
      :class="{ collapsed: isCollapsed }"
  >
    <div class="board-content" :style="{ width: boardWidth, height: boardHeight }">
      <div class="operate-btns">
        <button class="operate-btn" @click="toggleCollapse">
          {{ isCollapsed ? '口' : '一' }}
        </button>
        <button class="operate-btn close-btn" @click="closeBoard">×</button>
      </div>

      <div class="board-main" v-show="!isCollapsed">
        <canvas
            ref="canvas"
            class="drawing-canvas"
            @mousedown="startDrawing"
            @mousemove="draw"
            @mouseup="stopDrawing"
            @mouseleave="stopDrawing"
            @touchstart="handleTouchStart"
            @touchmove="handleTouchMove"
            @touchend="stopDrawing"
        ></canvas>
        <div class="toolbar">
          <div class="tool-item">
            <label>粉筆顏色：</label>
            <input type="color" v-model="brushColor" class="color-picker" :disabled="isEraser">
          </div>
          <div class="tool-item">
            <label>粗細：</label>
            <input type="range" min="1" max="30" v-model.number="currentSize">
            <span>{{ currentSize }}px</span>
          </div>
          <button
              class="eraser-btn"
              @click="toggleEraser"
              :class="{ active: isEraser }"
          >
            橡皮擦
          </button>
          <button class="clear-btn" @click="clearCanvas">清空</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "DrawingBoard",
  props: {
    visible: { type: Boolean, default: false }
  },
  data() {
    return {
      isDrawing: false,
      brushColor: "#ffffff",
      brushSize: 2,     // 画笔默认2px
      eraserSize: 25,   // 橡皮擦默认25px
      ctx: null,
      lastX: 0,
      lastY: 0,
      isCollapsed: false,
      canvasData: null,
      isEraser: false
    };
  },
  computed: {
    boardWidth() {
      return this.isCollapsed ? "80px" : "500px";
    },
    boardHeight() {
      return this.isCollapsed ? "35px" : "70vh";
    },
    currentSize: {
      get() {
        return this.isEraser ? this.eraserSize : this.brushSize;
      },
      set(val) {
        if (this.isEraser) {
          this.eraserSize = val;
        } else {
          this.brushSize = val;
        }
      }
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.initCanvas();
          if (this.canvasData) this.restoreCanvas();
        });
      }
    },
    isCollapsed(newVal) {
      if (!newVal) {
        this.$nextTick(() => {
          this.initCanvas();
          this.restoreCanvas();
        });
      } else {
        this.saveCanvas();
      }
    }
  },
  mounted() {
    if (this.visible) this.initCanvas();
    window.addEventListener('resize', () => {
      if (this.visible && !this.isCollapsed) {
        this.initCanvas();
        this.restoreCanvas();
      }
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', () => {});
  },
  methods: {
    initCanvas() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;

      const container = canvas.parentElement;
      const canvasWidth = 500;
      const canvasHeight = container.clientHeight - 40;

      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      this.ctx = canvas.getContext("2d");
      this.ctx.fillStyle = "#2c3e50";
      this.ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      this.ctx.lineCap = "round";
      this.ctx.lineJoin = "round";
    },

    saveCanvas() {
      if (this.$refs.canvas) {
        this.canvasData = this.$refs.canvas.toDataURL();
      }
    },

    restoreCanvas() {
      if (!this.canvasData || !this.ctx) return;
      const img = new Image();
      img.onload = () => {
        const canvas = this.$refs.canvas;
        this.ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      };
      img.src = this.canvasData;
    },

    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
    },

    toggleEraser() {
      this.isEraser = !this.isEraser;
    },

    startDrawing(e) {
      this.isDrawing = true;
      const rect = this.$refs.canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      this.lastX = x;
      this.lastY = y;
    },

    draw(e) {
      if (!this.isDrawing) return;
      const rect = this.$refs.canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      this.ctx.beginPath();
      this.ctx.strokeStyle = this.isEraser ? "#2c3e50" : this.brushColor;
      this.ctx.lineWidth = this.isEraser ? this.eraserSize : this.brushSize;
      this.ctx.moveTo(this.lastX, this.lastY);
      this.ctx.lineTo(x, y);
      this.ctx.stroke();

      this.lastX = x;
      this.lastY = y;
    },

    stopDrawing() {
      this.isDrawing = false;
    },

    clearCanvas() {
      const canvas = this.$refs.canvas;
      if (!canvas || !this.ctx) return;
      this.ctx.fillStyle = "#2c3e50";
      this.ctx.fillRect(0, 0, canvas.width, canvas.height);
      this.canvasData = null;
    },

    handleTouchStart(e) {
      e.preventDefault();
      this.startDrawing(e.touches[0]);
    },

    handleTouchMove(e) {
      e.preventDefault();
      this.draw(e.touches[0]);
    },

    closeBoard() {
      this.clearCanvas();
      this.canvasData = null;
      this.$emit("close");
    }
  }
};
</script>

<style scoped>
.drawing-board-container {
  position: fixed;
  right: 20px;
  bottom: 66px;
  z-index: 1000;
  transition: all 0.3s;
  opacity: 0.7;
}

.board-content {
  background: #f6b305;
  border-radius: 8px;
  padding: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  position: relative;
  height: 100%;
  box-sizing: border-box;
}

.collapsed .board-content {
  padding: 0;
}

.operate-btns {
  position: absolute;
  top: 6px;
  right: 6px;
  display: flex;
  gap: 4px;
  z-index: 10;
}

.operate-btn {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: none;
  background: #f1f1f1;
  cursor: pointer;
}

.close-btn {
  background: #ff6b6b;
  color: #fff;
}

.board-main {
  margin-top: 20px;
  height: calc(100% - 20px);
  box-sizing: border-box;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 8px;
  padding: 6px 10px;
  background: #f1f1f1;
  border-radius: 6px;
  font-size: 14px;
}

.clear-btn {
  padding: 4px 8px;
  background: #ffb842;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.eraser-btn {
  padding: 4px 8px;
  background: #95a5a6;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
.eraser-btn.active {
  background: #e74c3c;
}

.drawing-canvas {
  background: #2c3e50;
  width: 100%;
  height: calc(100% - 40px);
  border-radius: 4px;
  display: block;
  cursor: crosshair;
}
</style>